// Collection kinds (slice 7a): presets of item types made in code on the
// server (GET /kinds), fixed vocabularies (GET /vocabularies/:name), and the
// option codes of preset selects. Values are stored as codes; labels are
// shown in the UI language.
import { useEffect, useState } from 'react';
import api from './config/api';

/**
 * The shipped presets' data sources, as GET /kinds lists them (phase 4 §1.3):
 * what presetSources() answers until /kinds has, so the first render is the
 * right one (no TMDB search appearing a moment late).
 */
const SHIPPED_SOURCES = { movie: ['tmdb'], tv: ['tmdb'], book: ['openlibrary', 'bnf', 'googlebooks'], videogame: ['igdb'] };
/** The kinds once GET /kinds answered (null before). */
let loadedKinds = null;

/** Words in a type's name that suggest it already holds what a model is for (accents and case ignored). */
const LOOKALIKE = {
  movie: /\b(movies?|films?|cinema|dvds?|blu-?rays?)\b/,
  tv: /\b(series?|tv|shows?|television|feuilletons?)\b/,
  household: /\b(household|maison|home|menage)\b/,
  book: /\b(books?|livres?|bibliotheque|library|romans?|novels?|bd|bandes? dessinees?)\b/,
  videogame: /\b(jeux? videos?|video ?games?|games?|jeux|consoles?|retro|ludotheque)\b/,
};
const plain = (text) => String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/**
 * The caller's own plain types (no model yet) whose name looks like the
 * model's (Movies, Films, Séries…): converting one of them is usually what
 * they want, not a second, empty type.
 */
export const lookalikeTypes = (itemTypes, presetId) => {
  const pattern = LOOKALIKE[presetId];
  if (!pattern) return [];
  return (itemTypes || []).filter(type => type.is_mine && !type.preset && pattern.test(plain(type.name)));
};

/** The UI language as the server's labels know it: fr or en. */
export const uiLang = (i18n) => ((i18n.resolvedLanguage || i18n.language || 'en').startsWith('fr') ? 'fr' : 'en');

/** An option's stored value: the string itself, or the code of {value, labels}. */
export const optionValue = (option) => (option && typeof option === 'object' ? option.value : option);

/** An option as shown: its label in the UI language, or the string itself. */
export const optionLabel = (option, lang) => {
  if (!option || typeof option !== 'object') return option;
  return (option.labels && (option.labels[lang] || option.labels.en)) || option.value;
};

/** The owner's display label of a code on this field (option_labels, slice 7b), or undefined. */
export const ownLabel = (field, code) => {
  const label = field && field.option_labels && field.option_labels[code];
  return typeof label === 'string' && label !== '' ? label : undefined;
};

/** An option of this field as shown: the owner's display label, else the model's in the UI language. */
export const fieldOptionLabel = (field, option, lang) => ownLabel(field, optionValue(option)) ?? optionLabel(option, lang);

/** The label of a stored select value (a code of a preset select is translated, or shown with the owner's label). */
export const selectLabel = (field, value, lang) => {
  const option = (field.options || []).find(o => optionValue(o) === value);
  return option ? fieldOptionLabel(field, option, lang) : value;
};

/** Fields the owner uses: "unused" ones (slice 7b) are hidden from forms and lists, their values kept. */
export const usedFields = (fields) => (fields || []).filter(field => !field.unused);

// One fetch per page load, shared by every component.
const cache = {};
const load = (path) => {
  if (!cache[path]) {
    cache[path] = Promise.resolve().then(() => api.get(path)).then(r => r.data).catch((error) => {
      delete cache[path];
      throw error;
    });
  }
  return cache[path];
};

/** The kinds and their presets ([] while loading). */
export function useKinds() {
  const [kinds, setKinds] = useState(loadedKinds || []);
  useEffect(() => {
    let alive = true;
    load('/kinds').then(data => {
      loadedKinds = Array.isArray(data) ? data : [];
      if (alive) setKinds(loadedKinds);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return kinds;
}

/** {kind labels, preset labels} of a type's preset, from the list of kinds. */
export const presetInfo = (kinds, presetId) => {
  for (const kind of kinds || []) {
    const preset = (kind.presets || []).find(p => p.id === presetId);
    if (preset) return { kind, preset };
  }
  return null;
};

/**
 * The data sources of a preset, in their default order ([] for none): from
 * the kinds (GET /kinds, `sources`), else the shipped presets' until they are
 * known. $kinds defaults to the kinds already loaded by any component.
 */
export const presetSources = (presetId, kinds = loadedKinds) => {
  if (!presetId) return [];
  const info = presetInfo(kinds, presetId);
  if (info) {
    const { preset } = info;
    return Array.isArray(preset.sources) ? preset.sources : (preset.source ? [preset.source] : []);
  }
  return SHIPPED_SOURCES[presetId] || [];
};

/** True when some source fills items of this preset (it then has an integration page). */
export const hasSources = (presetId, kinds) => presetSources(presetId, kinds).length > 0;

/** True when the source $sourceId fills items of this preset ('tmdb' for films and series). */
export const presetUses = (presetId, sourceId, kinds) => presetSources(presetId, kinds).includes(sourceId);

/** A vocabulary's entries [{code, labels}] ([] while loading or for none). */
export function useVocabulary(name) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!name) return undefined;
    let alive = true;
    load(`/vocabularies/${name}`).then(data => { if (alive) setItems(data.items || []); }).catch(() => {});
    return () => { alive = false; };
  }, [name]);
  return items;
}

/** The labels of codes, in the UI language — the owner's display labels first when a field is given (an unknown code shown as itself). */
export const vocabLabels = (items, codes, lang, field) => (Array.isArray(codes) ? codes : [])
  .map(code => {
    const own = ownLabel(field, code);
    if (own) return own;
    const entry = items.find(item => item.code === code);
    return entry ? (entry.labels[lang] || entry.labels.en) : String(code);
  });

/** The vocabularies a vocab field may name (the editor's choice). */
export const VOCABULARIES = ['tmdb_movie_genres', 'tmdb_tv_genres', 'languages', 'book_genres', 'game_genres', 'game_modes'];
